import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { FormGroup, NonNullableFormBuilder, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

import { FilterConfig, FilterInputType } from './filters.model';
import { FilterDateRangeValidator } from './validators/filters-date-range.validator';

@Component({
  selector: 'flow-filters',
  templateUrl: './filters.component.html',
  styleUrls: ['./filters.component.scss'],
})
export class FiltersComponent implements OnInit, OnDestroy {
  @Input() filters: FilterConfig[] = [];
  @Input() submitBtnText: string = 'global.show';
  @Input() clearBtnText: string = 'global.clearAll';
  @Input() clearBtn: boolean = false;
  @Output() filtersChanged = new EventEmitter<Record<string, any>>();

  @Input() form: FormGroup = new FormGroup({});
  visibleFilters: FilterConfig[] = [];
  private destroy$ = new Subject<void>();
  private isUpdatingFilters = false;
  private controlSubscriptions = new Map<string, Subject<void>>();
  FilterInputType = FilterInputType;

  constructor(private fb: NonNullableFormBuilder) {}

  ngOnInit(): void {
    // Check required validators
    this.checkRequired();

    // First, update filters to determine visibility
    this.updateFilters();

    // Then create form controls only for visible filters
    this.createForm();

    // Set initial values for visible controls
    this.setInitialValues();

    // Finally, subscribe to dependent controls
    this.subscribeToDependentControls();
  }

  // Create form controls only for visible filters
  private createForm(): void {
    this.visibleFilters.forEach(f => {
      if (!this.form.contains(f.controlName)) {
        const control = this.fb.control(f.value ?? null, f.validators || []);
        this.form.addControl(f.controlName, control);
      }
    });
  }

  setVisibleDateGroups(): void {
    const visibleDateGroups = {};
    this.filters.forEach(f => {
      if (f.type === FilterInputType.Date && f.dateRangeGroupKey) {
        visibleDateGroups[f.dateRangeGroupKey] = visibleDateGroups[f.dateRangeGroupKey] || [];
        visibleDateGroups[f.dateRangeGroupKey].push(f.controlName);
      }
    });

    // set date range validation for date range group
    this.dateRangeValidation(visibleDateGroups);
  }

  private setVisibleFilters(): void {
    const currentVisibleControls = this.visibleFilters.map(f => f.controlName);

    // The visible property is automatically set based on filter dependencies
    this.filters.forEach(f => {
      if (f.dependsOn && f.dependsOn.length > 0) {
        f.visible = this.isDependencyMet(f);
      }
    });

    const { visibleFilters } = this.filters
      .filter(f => f.visible)
      .reduce(
        (acc, f) => {
          acc.visibleFilters.push(f);
          return acc;
        },
        {
          visibleFilters: [] as FilterConfig[],
        }
      );

    this.visibleFilters = [...visibleFilters];

    // Add new controls
    visibleFilters.forEach(f => {
      if (!this.form.contains(f.controlName)) {
        const control = this.fb.control(f.value ?? null, f.validators || []);
        this.form.addControl(f.controlName, control);
        f.dependsOn && this.subscribeToControlValueChanges(f.controlName);
      }
    });

    // Remove controls
    currentVisibleControls.forEach(key => {
      if (!visibleFilters.map(f => f.controlName).includes(key)) {
        this.unsubscribeToControlValueChanges(key);
        this.form.removeControl(key);
      }
    });
  }

  unsubscribeToControlValueChanges(controlKey: string): void {
    const destroy$ = this.controlSubscriptions.get(controlKey);
    if (destroy$) {
      destroy$.next();
      destroy$.complete();
      this.controlSubscriptions.delete(controlKey);
    }
  }

  // Set initial values for visible filters
  setInitialValues() {
    this.visibleFilters.forEach(f => {
      const control = this.form.get(f.controlName);
      if (control && f.value !== undefined) {
        control.setValue(f.value, { emitEvent: false });
      }
    });
  }

  // Check if the filter is required
  checkRequired() {
    this.filters.forEach(f => {
      const hasRequiredValidator = f.validators && f.validators.includes(Validators.required);
      f.required = hasRequiredValidator || false;
    });
  }

  updateFilters(): void {
    if (this.isUpdatingFilters) {
      return; // Prevent recursive calls
    }

    this.isUpdatingFilters = true;

    try {
      this.setVisibleFilters();
      this.setVisibleDateGroups();
      this.updateDisabledStatus();
    } finally {
      this.isUpdatingFilters = false;
    }
  }

  private updateDisabledStatus(): void {
    // Update disabled state of form controls
    this.filters.forEach(f => {
      const control = this.form.get(f.controlName);
      if (control) {
        if (f.disabled) {
          control.disable({ emitEvent: false });
        } else {
          control.enable({ emitEvent: false });
        }
      }
    });

    this.form.updateValueAndValidity();
  }

  private dateRangeValidation(visibleDateGroups: Record<string, string[]>): void {
    this.form.clearValidators();

    // Apply date range validation for grouped date fields
    Object.entries(visibleDateGroups).forEach(([groupKey, keys]) => {
      if (keys.length === 2) {
        // Find the first filter with this group key to get validation config
        const filterWithConfig = this.filters.find(f => f.dateRangeGroupKey === groupKey && f.dateRangeValidation);
        this.form.setValidators(FilterDateRangeValidator(keys[0], keys[1], filterWithConfig.dateRangeValidation));
      }
    });
  }

  applyFilters() {
    if (this.form.valid) {
      this.filtersChanged.emit(this.form.value);
    } else {
      // Mark all fields as touched to show validation errors
      Object.keys(this.form.controls).forEach(key => {
        const control = this.form.get(key);
        control?.markAsTouched();
      });
    }
  }

  resetFilters() {
    this.form.reset({ emitEvent: false });
  }

  private subscribeToDependentControls(): void {
    // Get unique control keys that other filters depend on
    const dependentControlKeys = new Set<string>();

    this.filters.forEach(filter => {
      if (filter.dependsOn) {
        filter.dependsOn.forEach(d => dependentControlKeys.add(d.key));
      }
    });

    // Subscribe to control's valueChanges
    dependentControlKeys.forEach(controlKey => {
      this.subscribeToControlValueChanges(controlKey);
    });
  }

  private isDependencyMet(filter: FilterConfig): boolean {
    if (!filter.dependsOn || filter.dependsOn.length === 0) {
      return true;
    }

    // Group dependencies by their key to handle multiple dependencies from the same parent
    // A control can have multipe dependencies
    const dependenciesByKey: { key: string; values: any[] }[] = [];

    filter.dependsOn.forEach(dependency => {
      const existingGroup = dependenciesByKey.find(group => group.key === dependency.key);
      if (existingGroup) {
        existingGroup.values.push(dependency.value);
      } else {
        dependenciesByKey.push({ key: dependency.key, values: [dependency.value] });
      }
    });

    // Check if dependencies are met
    return dependenciesByKey.every(group => {
      // First try to get the value from the form control
      let controlValue = this.form.get(group.key)?.value;

      // If the control doesn't exist or has no value, try to get the initial value from the filter config
      if (controlValue == null) {
        const dependentFilter = this.filters.find(f => f.controlName === group.key);
        controlValue = dependentFilter?.value;
      }

      // Handle cases where both values might be null/undefined
      if (controlValue == null && group.values.some(v => v == null)) {
        return true;
      }

      // Handle cases where one value is null/undefined and the other isn't
      if (controlValue == null || group.values.every(v => v == null)) {
        return false;
      }

      // For arrays, check if any of the expected values are included
      if (Array.isArray(controlValue)) {
        return group.values.some(expectedValue => controlValue.includes(expectedValue));
      }

      // For primitive values, check if the control value matches any of the expected values
      return group.values.some(expectedValue => controlValue === expectedValue);
    });
  }

  // Add this method to subscribe to a specific control's value changes
  private subscribeToControlValueChanges(controlKey: string): void {
    const controlDestroy$ = new Subject<void>();
    const control = this.form.get(controlKey);

    this.controlSubscriptions.set(controlKey, controlDestroy$);

    if (control) {
      control.valueChanges.pipe(takeUntil(controlDestroy$)).subscribe(() => {
        this.updateFilters();
      });
    }
  }

  ngOnDestroy(): void {
    // Clean up all control-specific subscriptions
    this.controlSubscriptions.forEach(subscription => {
      subscription.next();
      subscription.complete();
    });

    this.controlSubscriptions.clear();

    this.destroy$.next();
    this.destroy$.complete();
  }
}
